import { LitElement, TemplateResult, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import innerCSS from './se-jump-nav.inner.pcss?inline';
import { whenParsed } from '../../utilities/when-parsed';

import '../se-container/se-container';

export type SeJumpNavJumps = { [key: string]: string };
export type SeJumpNavVisibility = { [key: string]: number };

/**
 * The SeJumpNav component
 * @element se-jump-nav
 */
@customElement('se-jump-nav')
export class SeJumpNav extends LitElement {
  private stylesheetController = new AdoptedStyleSheets(this, {
    encapsulatedCSS: innerCSS,
  });

  resizeObserver: ResizeObserver;
  loadHandler: () => void;
  pollingInterval: number;

  /**
   * ID of "active" element.
   */
  @property({ type: String })
  isActive: string;

  /**
   * Jump target id and their % visible on screen.
   * */
  @state()
  visibility: SeJumpNavVisibility = {
    overview: 0,
    experience: 0,
    insights: 0,
    awards: 0,
    services: 0,
    industries: 0,
    news: 0,
    events: 0,
    lodging: 0,
    parking: 0,
  };

  /**
   * Expected link IDs and their titles.
   */
  jumps: SeJumpNavJumps = {
    overview: 'overview',
    experience: 'key experience',
    insights: 'insights',
    awards: 'awards & accolades',
    services: 'related services',
    industries: 'related industries',
    news: 'news',
    events: 'events',
    lodging: 'lodging & dining',
    parking: 'parking',
  };

  /**
   * Current height of the jump-nav. Used to determine true viewable space.
   */
  navOffset: number;

  /**
   * Current height of the main-nav. Used to determine true viewable space.
   */
  headerOffset: number;

  /**
   * Height of any hero on the page/the position that the jump-nav is supposed to switch to fixed/sticky position.
   */
  triggerFixedOffset: number;

  /**
   * Wether or not the nav is in 'fixed' position.
   */
  fixed: boolean;

  /**
   * If the users browser is set to 'prefers-reduced-motion' will prevent scrolling and cleanly jump to selected section.
   */
  preventScroll: boolean;

  render(): TemplateResult {
    return html`<section class="se-jump-nav">
      <se-container class="se-jump-nav--container">
        <nav class="se-jump-nav--nav" aria-label="jump navigation">
          <ul class="se-jump-nav--list">
            ${this.parsed ? this.generateLinks() : nothing}
          </ul>
        </nav>
      </se-container>
    </section>`;
  }
  /**
   * Whether the document has finished parsing.
   *
   * generateLinks() scans the whole document for the section ids it links to,
   * and those sections come after this element in source order. In the
   * critical bundle this component upgrades mid-parse, so that scan would find
   * none of them -- and cleanState() *deletes* every id it cannot find from
   * this.jumps, so the loss is permanent and no later re-render can undo it.
   * Links are therefore withheld until parsing completes. See
   * utilities/when-parsed.ts (SE-1004).
   */
  @state() private parsed = false;

  async firstUpdated(): Promise<void> {
    await whenParsed();
    this.parsed = true;

    this.setOffsets();
    this.setReduceMotion();
    this.determineViewStatus();
    this.resizeObserver.observe(this);

    // Also observe hero and header elements for size changes
    const hero = document.querySelector(
      'location-hero, capability-hero, landing-hero'
    );
    if (hero) {
      this.resizeObserver.observe(hero);
    }

    const header = document.querySelector('se-main-header');
    if (header) {
      this.resizeObserver.observe(header);
    }
  }

  updated() {
    this.setOffsets();
  }

  connectedCallback(): void {
    super.connectedCallback();

    this.resizeObserver = new ResizeObserver(() => {
      this.setOffsets();
    });

    // Store reference to load handler for cleanup
    this.loadHandler = () => {
      this.setOffsets();
      // Continue polling for 15 seconds after page load, then stop
      setTimeout(() => {
        if (this.pollingInterval) {
          clearInterval(this.pollingInterval);
          this.pollingInterval = undefined;
        }
      }, 15000);
    };

    window.addEventListener('scroll', this.determineViewStatus);
    window.addEventListener('scroll', this.toggleFixedPosition);

    // Recalculate offsets after page fully loads (including images)
    window.addEventListener('load', this.loadHandler);

    // Poll every 500ms during page load to catch race conditions
    // This will auto-stop when page load event fires
    this.pollingInterval = window.setInterval(() => {
      this.setOffsets();
    }, 500);
  }
  disconnectedCallback(): void {
    window.removeEventListener('scroll', this.determineViewStatus);
    window.removeEventListener('scroll', this.toggleFixedPosition);
    window.removeEventListener('load', this.loadHandler);
    this.resizeObserver.unobserve(this);

    // Clean up polling interval if it's still running
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = undefined;
    }

    super.disconnectedCallback();
  }

  setReduceMotion() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.preventScroll = true;
    }
  }

  /**
   * Sets the css var for the navs fixed position.
   */
  setTopVar(height: number) {
    // Less 1 px to handle occasional .5 px discrepancy.
    this.style.setProperty('--top-offset', `${height - 1}px`);
  }

  /**
   * Filters through the expected links (see this.visibility, this.jumps)
   * and determines if the associated elements are on the page, creating the links for all present,
   * and removing references to those that may not be.
   *
   */
  generateLinks() {
    const existingIds = Object.keys(this.jumps).map(id => {
      if (document.querySelectorAll(`#${id}`).length > 0) {
        return id;
      } else {
        return this.cleanState(id);
      }
    });
    return existingIds
      .filter(el => el !== undefined)
      .map(
        link => html`<li class="se-jump-nav--item" @click=${this.scrollHandler}>
          <a class="se-jump-nav--link" id="${link}-jump" href="#${link}"
            >${this.jumps[link!].toUpperCase()}</a
          >
        </li>`
      );
  }

  /**
   * In case of error, removes element from expected links if id can't be found on page.
   */
  cleanState(id: string) {
    delete this.jumps[id];
    delete this.visibility[id];
  }

  /**
   * On click "scroll handler" to initiate scrolling when jump link is clicked.
   */
  scrollHandler(e: Event) {
    e.preventDefault();
    const target = e.target as HTMLAnchorElement;
    const targetHref = target.getAttribute('href');
    const scrollTarget = document.querySelector(`${targetHref}`) as HTMLElement;
    if (scrollTarget) {
      const correctedTop = scrollTarget.offsetTop - this.navOffset;
      window.scroll({
        top: correctedTop,
        behavior: this.preventScroll ? 'auto' : 'smooth',
      });
    }
  }

  /**
   * If an element has moved in/out of view, updates the state object and calls
   * this.setActiveOnStateUpdate to determine if the active link should be changed.
   */
  updateState(id: string, percentage: number) {
    if (this.visibility[id] !== percentage) {
      this.visibility[id] = percentage;

      this.setActiveOnStateUpdate();
    }
  }

  /**
   * Called when the component reaches/exceeds triggerFixedOffset point and applies/removes is-fixed class.
   */
  toggleFixedPosition() {
    const host = document.querySelector('se-jump-nav') as SeJumpNav;
    const nav = host?.shadowRoot?.querySelector('.se-jump-nav') as HTMLElement;

    if (nav && window.scrollY >= host.triggerFixedOffset) {
      nav.classList.add('is-fixed');
      host.fixed = true;
    }
    if (nav && window.scrollY < host.triggerFixedOffset && host.fixed) {
      nav.classList.remove('is-fixed');
      host.fixed = false;
    }
  }

  /**
   * Determines headerOffset, topOffset, and triggerFixedOffset properties for use in calculations.
   */
  setOffsets() {
    const header = document.querySelector('se-main-header');
    const headerHeight = header ? header.getBoundingClientRect().height : 145;
    const hero = document.querySelector(
      'location-hero, capability-hero, landing-hero'
    );
    const heroHeight = hero ? hero?.getBoundingClientRect().height : 600;
    const nav = document.querySelector('se-jump-nav');
    this.navOffset = nav ? nav.getBoundingClientRect().height : 52;

    // Store previous values to detect changes
    const prevHeaderOffset = this.headerOffset;

    this.headerOffset = headerHeight;
    this.triggerFixedOffset = heroHeight;

    // Always use the newly calculated headerHeight, not the potentially stale this.headerOffset
    this.setTopVar(headerHeight);

    // If header offset changed and we're in fixed mode, recheck fixed position
    if (prevHeaderOffset !== undefined && prevHeaderOffset !== headerHeight && this.fixed) {
      this.toggleFixedPosition();
    }
  }

  /**
   * Passes all present elements with jump links to this.isInView.
   */
  determineViewStatus() {
    const home = document.querySelector('se-jump-nav') as SeJumpNav;

    Object.keys(home.jumps).forEach(el => {
      const element = document.querySelector(`#${el}`) as HTMLElement;
      return home.isInView(element);
    });
  }

  /**
   * Takes in an element and returns an object comprised of several positioning values used in multiple methods.
   */
  getPositioningValues(el: HTMLElement) {
    const windowTop = window.scrollY;
    const windowBottom = windowTop + window.innerHeight;
    const elRect = el.getBoundingClientRect();
    const elTop = elRect.y + windowTop - (this.navOffset + this.headerOffset);
    const elBottom = elRect.y + elRect.height + windowTop;
    const elHeight = elRect.height;

    return {
      windowTop: windowTop,
      windowBottom: windowBottom,
      elTop: elTop,
      elBottom: elBottom,
      elHeight: elHeight,
    };
  }

  /**
   * Determines if an element is in view or not.
   * If only partially in view passes the element id to this.elementXPercentInViewport.
   */
  isInView(el: HTMLElement) {
    const { elTop, elBottom, windowTop, windowBottom } =
      this.getPositioningValues(el);

    const isVis = !(elTop > windowBottom || elBottom < windowTop);
    const notVis = elTop > windowBottom || elBottom < windowTop;
    const allVis =
      (elTop >= windowTop && elBottom <= windowBottom) ||
      (elTop < windowTop && elBottom > windowBottom);

    if (notVis) {
      return this.updateState(el.id, 0);
    }

    if (allVis) {
      return this.updateState(el.id, 100);
    }

    if (isVis) {
      return this.elementXPercentInViewport(el);
    }
  }

  /**
   * Determines the percentage of the element in view.
   */
  elementXPercentInViewport(el: HTMLElement) {
    const { elTop, elBottom, elHeight, windowTop, windowBottom } =
      this.getPositioningValues(el);

    if (elBottom > windowBottom) {
      const visPx = elHeight - (elBottom - windowBottom);
      return this.updateState(el.id, Math.round((visPx / elHeight) * 100));
    }
    if (elTop < windowTop) {
      const visPx = elHeight - (windowTop - elTop);
      return this.updateState(el.id, Math.round((visPx / elHeight) * 100));
    }
  }

  /**
   * When the visibility state object is updated Compares % visible values and either passes the
   * most visible elements ID to this.setActive or if more than one have the same value
   * passes them to this.getTopPositions to determine which is the highest on the page.
   */
  setActiveOnStateUpdate() {
    const hightestPercentageVisible = Math.max(
      ...Object.values(this.visibility)
    );

    const mostVisibleElements = Object.entries(this.visibility).filter(
      ele => ele[1] === hightestPercentageVisible
    );

    if (mostVisibleElements.length > 1) {
      return this.getTopPositions(mostVisibleElements);
    } else {
      const id: string = mostVisibleElements[0][0];
      return this.setActive(id);
    }
  }

  /**
   * Sorts through multiple elements that are the same percentage in view, and passes the if of the element highest on the page to setActive.
   */
  getTopPositions(ids: [string, number][]) {
    const topPositions: { [key: string]: number } = {};
    ids.map(ele => {
      if (document.querySelector(`#${ele[0]}`)?.getBoundingClientRect().top) {
        const id = ele[0] as string;
        // @ts-expect-error - because-ts
        return (topPositions[id] = document
          .querySelector(`#${ele[0]}`)
          ?.getBoundingClientRect().top);
      } else {
        return null;
      }
    });
    const activeID = Object.keys(topPositions).find(
      key => topPositions[key] === Math.min(...Object.values(topPositions))
    )!;

    return this.setActive(activeID);
  }

  /**
   * Takes an ID and if not already the active ID, sets it as this.isActive, then handles the passing of the active-jump class to the correct link.
   */
  setActive(id: string) {
    if (this.isActive !== id) {
      this.isActive = id;
      this.shadowRoot
        ?.querySelector(`.active-jump`)
        ?.classList.remove('active-jump');
      this.shadowRoot
        ?.querySelector(`#${id}-jump`)
        ?.classList.add('active-jump');
    }
  }
}
